import { useQuery } from '@tanstack/react-query';
import { ISelectedFilterBody } from 'components/filter/models/TableFilterModels';
import { requestParseTime } from 'components/filter/utils/requestParseTime';
import { ModalConfirmCreateHierarchy } from 'components/modal/ModalConfirmCreateHierarchy';
import moment from 'moment';
import { useCreateOpportunity } from 'pages/admin/demand/opportunity/hooks/useCreateOpportunity';
import { useEditOpportunity } from 'pages/admin/demand/opportunity/hooks/useEditOpportunity';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getMembers } from 'services/api/requests/requests';
import { useGetCheckTableDataWithFilters } from 'services/queryClient/wrapperHooks/genericTables/useGetCheckTableDataWithFilters';
import { useSalesChannelList } from 'services/queryClient/wrapperHooks/salesChannel/useSalesChannelList';
import { useGetFilterCommercialHierarchy } from 'services/queryClient/wrapperHooks/useGetFilterCommercialHierarchy';
import { useSalesForceList } from 'services/queryClient/wrapperHooks/useSalesForceList';
import {
	renderClient,
	renderFamily,
	renderProduct,
} from 'utils/forms/autocomplete';
import { currencyToNumber } from 'utils/functions/currencyToNumber';
import { filterNotInformedOptions } from 'utils/functions/filterNotInformedOptions';

import { useProductFamilyLogic } from '../../hooks/useProductFamilyLogic';
import { useSalesZoneList } from '../../services/queryClient/wrapperHooks/useSalesZoneList';
import { makeGenericFormInputStructure } from '../../utils/forms/defaultInputs';
import {
	columnFlexProps,
	defaultFormStyle,
	defaultInputStyle,
	formFlexProps,
	rowFlexProps,
} from '../../utils/forms/defaultsProps';
import {
	validateByRegex,
	validateMin,
	validateMinDecimal,
	validateRequired,
} from '../../utils/forms/validations';
import { LoadingSpinner } from '../loading/Loading';
import GenericForm, { IFormRows } from './GenericForm';

interface IFormCadastrarEditarOportunidade {
	initialState: any;
	opportunityId?: number;
	autocompleteAdditionalData?: any;
}

interface ICycle {
	id: number;
	mes_ano: string;
	ciclo: number;
	ano: number;
	data_inicio: string;
}

const FormCadastrarEditarOportunidade = ({
	initialState,
	opportunityId = 0,
	autocompleteAdditionalData,
}: IFormCadastrarEditarOportunidade) => {
	const history = useHistory();

	const isEditing = !!opportunityId;

	const { data: SalesChannelData, isLoading: isLoadingSalesChannelData } =
		useSalesChannelList();
	const { data: SalesZoneData, isLoading: IsLoadingSalesZone } =
		useSalesZoneList();
	const {
		data: CommercialHierarchyData,
		isLoading: isLoadingCommercialHierarchy,
	} = useGetFilterCommercialHierarchy(undefined);
	const { data: SalesForceData, isLoading: isLoadingSalesForce } =
		useSalesForceList();

	const [formInitialState, setFormInitialState] = useState<any>();

	const [salesChannelId, setSalesChannelId] = useState<number>();
	const [forceFamilyValue, setForceFamilyValue] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [pendingPayload, setPendingPayload] = useState<any>(null);

	const {
		familyId,
		selectedProduct,
		handleOnChangeFamily,
		handleOnChangeProduct,
		isDisabledProductAutocomplete,
		shouldClearAutocompleteProduct,
	} = useProductFamilyLogic({
		initialFamilyId: initialState?.familia_id,
		initialProduct: autocompleteAdditionalData?.product,
	});
	const [cycleFilter, setCycleFilter] = useState<ISelectedFilterBody[] | []>([
		{
			column_name: 'mes_ano',
			value_type: 'string',
			values: moment().format('MM/YYYY'),
			operation: 'mes_ano',
			prioritario: true,
		},
	]);

	const filterBySalesChannel = isEditing ? false : true;

	const { data: MembersData, isLoading: isLoadingMemberData } = useQuery(
		['member-list-filteredSales', filterBySalesChannel, salesChannelId],
		getMembers,
		{
			// Propriedade de opção para no caso de true, atualizar a página sempre que o usuário troca de aba e/ou colocar a tela em foco.
			refetchOnWindowFocus: false,
		}
	);

	const { data: dataCheckTableForCycle } = useGetCheckTableDataWithFilters({
		module: 'ciclo',
		page: 0,
		size: 1000,
		filters: requestParseTime(cycleFilter),
	});

	const handleOpenModal = useCallback(() => {
		setIsModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setIsModalOpen(false);
		setPendingPayload(null);
	}, []);

	const { mutate: createOpportunity } = useCreateOpportunity(handleOpenModal);
	const { mutate: updateOpportunity } = useEditOpportunity(
		opportunityId,
		handleOpenModal
	);

	const handleConfirmCreateHierarchy = useCallback(() => {
		if (pendingPayload) {
			const bodyWithCreate = {
				...pendingPayload,
				is_create_hierarchy: true,
			};
			if (isEditing) {
				updateOpportunity(bodyWithCreate);
			} else {
				createOpportunity(bodyWithCreate);
			}
		}
		setIsModalOpen(false);
	}, [pendingPayload, isEditing, createOpportunity, updateOpportunity]);

	useEffect(() => {
		setSalesChannelId(initialState?.canal_venda_id);
	}, [initialState]);

	useEffect(() => {
		setFormInitialState(initialState);
	}, [initialState]);

	useEffect(() => {
		if (!isEditing) {
			setFormInitialState({
				...initialState,
				ciclo_id: dataCheckTableForCycle?.data[0]?.id || null,
			});
		}
	}, [initialState, dataCheckTableForCycle?.data, isEditing]);

	const findMemberById = (membersData, memberId) => {
		return membersData.find(member => member?.id === memberId);
	};

	useEffect(() => {
		if (MembersData) {
			const matchingMember = findMemberById(
				MembersData?.data?.rows,
				initialState?.membro_id
			);
			if (matchingMember) {
				setMemberBelongsToChannel(matchingMember.membro_pertence_canal);
			}
		}
	}, [MembersData]);

	const goToOpportunities = useCallback(() => {
		history.goBack();
	}, [history]);

	const marketOptions = useMemo(() => {
		const filteredMarkets = filterNotInformedOptions(
			CommercialHierarchyData?.mercados || [],
			'nome_mercado'
		);

		return filteredMarkets.map(m => (
			<option
				key={m?.id}
				value={m?.id}
			>
				{m?.nome_mercado}
			</option>
		));
	}, [CommercialHierarchyData]);

	const salesChannelOptions = useMemo(() => {
		return (
			SalesChannelData?.map(channel => {
				return (
					<option
						key={channel?.id}
						value={channel?.id}
					>
						{channel?.nome_canal_venda}
					</option>
				);
			}) || []
		);
	}, [SalesChannelData]);

	const salesForceOptions = useMemo(() => {
		const filteredSalesForce = filterNotInformedOptions(
			SalesForceData?.data || [],
			'nome_forca_venda'
		);

		return filteredSalesForce.map(sf => (
			<option
				key={sf?.id}
				value={sf?.id}
			>
				{sf?.nome_forca_venda}
			</option>
		));
	}, [SalesForceData]);

	const zoneOptions = useMemo(() => {
		const filteredZones = filterNotInformedOptions(
			SalesZoneData?.data || [],
			'nome_zona_venda'
		);

		return filteredZones.map(s => (
			<option
				key={s?.id}
				value={s?.id}
			>
				{s?.nome_zona_venda}
			</option>
		));
	}, [SalesZoneData]);

	const memberOptions = useMemo(() => {
		return (
			MembersData?.data?.rows.map(member => (
				<option
					key={member?.id}
					value={member?.id}
				>
					{member?.nome_membro}
				</option>
			)) || []
		);
	}, [MembersData, salesChannelId]);

	useEffect(() => {
		if (isEditing && formInitialState?.ciclo_id) {
			setCycleFilter([
				{
					column_name: 'id',
					value_type: 'number',
					values: formInitialState?.ciclo_id,
					operation: 'equal',
					prioritario: true,
				},
				{
					column_name: 'mes_ano',
					value_type: 'string',
					values: moment().format('MM/YYYY'),
					operation: 'mes_ano',
					prioritario: true,
				},
			]);
		}
	}, [formInitialState, isEditing]);

	const cycleYearOptions = useMemo(() => {
		if (dataCheckTableForCycle?.data) {
			return (
				dataCheckTableForCycle?.data?.map((cycle: ICycle) => {
					return (
						<option
							key={cycle.id}
							value={cycle.id}
						>
							{cycle.mes_ano}
						</option>
					);
				}) || null
			);
		}
	}, [dataCheckTableForCycle, formInitialState, isEditing]);

	const [memberBelongsToChannel, setMemberBelongsToChannel] = useState<
		null | boolean
	>(true);

	const handleOnChangeCode = useCallback((event, form) => {
		form.setValue('sequencia_codigo', '');
	}, []);

	const handleOnChangeSalesChannel = useCallback(event => {
		const valueSelected = Number(event.target.value);
		setSalesChannelId(valueSelected);
	}, []);

	const handleOnChangeMember = useCallback(
		event => {
			const memberId = event?.target.value;

			const matchingMember = findMemberById(
				MembersData?.data?.rows,
				memberId
			);

			if (matchingMember) {
				setMemberBelongsToChannel(matchingMember.membro_pertence_canal);
			}

			if (!memberId) {
				setMemberBelongsToChannel(true);
			}
		},
		[MembersData]
	);

	const handleOnChangeFamilyWithLog = useCallback(
		(value: any) => {
			handleOnChangeFamily(value);
		},
		[handleOnChangeFamily]
	);

	const handleOnChangeProductWithLog = useCallback(
		(value: any, formProps: any) => {
			handleOnChangeProduct(value);

			if (value?.familia_id && value?.nome_familia) {
				const familyData = {
					id: value.familia_id,
					nome_familia: value.nome_familia,
					empresa_id: value.empresa_id,
				};

				setForceFamilyValue(familyData);
				handleOnChangeFamilyWithLog(familyData);

				if (formProps) {
					formProps.setValue('familia_id', value.familia_id);
				}

				setTimeout(() => {
					setForceFamilyValue(null);
				}, 100);
			}
		},
		[handleOnChangeProduct, handleOnChangeFamilyWithLog]
	);

	const isDisabledMemberSelect = useCallback(() => {
		return !salesChannelId;
	}, [salesChannelId]);

	const onSubmit = useCallback(
		values => {
			const body = {
				data: values?.data,
				ciclo_id: Number(values?.ciclo_id),
				codigo: values?.codigo,
				sequencia_codigo: values?.sequencia_codigo,
				cliente_id: values?.cliente_id,
				cnpj_cpf: values?.cnpj_cpf,
				familia_id: values?.familia_id,
				produto_id: values?.produto_id,
				codigo_produto: values?.codigo_produto,
				quantidade: currencyToNumber(values?.quantidade),
				canal_venda_id: Number(values?.canal_venda_id),
				mercado_id: Number(values?.mercado_id),
				forca_venda_id: Number(values?.forca_venda_id),
				zona_venda_id: Number(values?.zona_venda_id),
				membro_id: values.membro_id,
				receita_bruta: currencyToNumber(values?.receita_bruta),
			};

			setPendingPayload(body);

			if (isEditing) updateOpportunity(body);
			else createOpportunity(body);
		},
		[createOpportunity, isEditing, updateOpportunity]
	);

	const renderRows = useCallback((): IFormRows[] => {
		return [
			{
				type: 'common',
				rowFlexProps: {
					flexDirection: 'row',
					width: '100%',
				},
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'text',
							name: 'codigo',
							label: 'Código da Oportunidade',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
							inputProps: {
								'data-test':
									'input-codigo_da_oportunidade-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								onChange: handleOnChangeCode,
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'number',
							name: 'sequencia_codigo',
							label: 'Sequência do Cód. da Oportunidade',
							validate: {
								...validateMin(
									1,
									'O valor deve ser maior ou igual a 1!'
								),
								...validateByRegex(
									/^[1-9]\d*$/,
									'O valor deve ser um número inteiro válido!'
								),
							},
							inputProps: {
								'data-test':
									'input-sequencia_do_cod_da_oportunidade-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'date',
							name: 'data',
							label: 'Competência',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'input-competencia-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'ciclo_id',
							label: 'Ciclo/Ano',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
							},
							inputProps: {
								'data-test':
									'select-ciclo_ano-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								selectOptions: cycleYearOptions,
								disabled: !isEditing,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'familia_id',
							label: 'Família',
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
							inputProps: {
								'data-test':
									'autocomplete-familia-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								searchType: 'familia',
								renderInputLabel: renderFamily,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.family
									? renderFamily(
											autocompleteAdditionalData?.family
									  )
									: '',
								onChange: handleOnChangeFamilyWithLog,
								forceValue: forceFamilyValue,
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'produto_id',
							label: 'Produto',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							disabled: isDisabledProductAutocomplete,
							inputProps: {
								'data-test':
									'autocomplete-produto-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								searchType: 'produto',
								renderInputLabel: renderProduct,
								autocomplete: 'off',
								initialLabel:
									autocompleteAdditionalData?.product
										? renderProduct(
												autocompleteAdditionalData?.product
										  )
										: '',
								initialValue: selectedProduct,
								extraRequestData: {
									familia_id: familyId,
								},
								clearInput: shouldClearAutocompleteProduct,
								onChange: handleOnChangeProductWithLog,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'cliente_id',
							label: 'Razão Social',
							showRequiredOnLabel: true,
							inputProps: {
								'data-test':
									'autocomplete-cliente-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								searchType: 'cliente',
								renderInputLabel: renderClient,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.client
									? renderClient(
											autocompleteAdditionalData?.client
									  )
									: '',
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'product-quantity',
							name: 'quantidade',
							label: 'Quantidade',
							showRequiredOnLabel: true,
							validate: {
								...validateRequired(),
								validate: value =>
									validateMinDecimal(
										value,
										0,
										'O valor deve ser maior ou igual a 0!'
									),
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
							inputProps: {
								'data-test':
									'input-quantidade-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'decimal',
							name: 'receita_bruta',
							label: 'Receita bruta',
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'input-receita_bruta-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'mercado_id',
							label: 'Mercado',
							inputProps: {
								'data-test':
									'select-mercado-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								selectOptions: marketOptions,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'canal_venda_id',
							label: 'Canal de venda',
							showRequiredOnLabel: false,
							inputProps: {
								'data-test':
									'select-canal_de_venda-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								selectOptions: salesChannelOptions,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'forca_venda_id',
							label: 'Força de Venda',
							inputProps: {
								'data-test':
									'select-forca_venda-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								selectOptions: salesForceOptions,
							},
							columnFlexProps: {
								...columnFlexProps,
								marginRight: '30px',
							},
						}),
					},
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'zona_venda_id',
							label: 'Zona de Venda',
							inputProps: {
								'data-test':
									'select-zona_de_venda-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								selectOptions: zoneOptions,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'membro_id',
							label: 'Membro',
							inputProps: {
								'data-test':
									'select-membro-cadastrar_editar-page-oportunidades',
								...defaultInputStyle,
								selectOptions: memberOptions,
							},
						}),
					},
				],
			},
		];
	}, [
		autocompleteAdditionalData,
		salesChannelId,
		familyId,
		selectedProduct,
		handleOnChangeSalesChannel,
		handleOnChangeFamilyWithLog,
		handleOnChangeProductWithLog,
		isDisabledMemberSelect,
		isDisabledProductAutocomplete,
		salesChannelOptions,
		memberOptions,
		shouldClearAutocompleteProduct,
		memberBelongsToChannel,
		isEditing,
		cycleYearOptions,
		marketOptions,
		salesForceOptions,
		zoneOptions,
		forceFamilyValue,
	]);

	const renderForm = useCallback(() => {
		if (
			isLoadingSalesChannelData ||
			IsLoadingSalesZone ||
			isLoadingCommercialHierarchy ||
			isLoadingSalesForce
		)
			return <LoadingSpinner />;

		return (
			<>
				<GenericForm
					dataTestProps={{
						cancel: 'button-cancelar-page-oportunidades',
						save: 'button-salvar-page-oportunidades',
					}}
					initialState={formInitialState}
					formFlexProps={formFlexProps}
					rows={renderRows()}
					style={defaultFormStyle}
					onSubmit={onSubmit}
					onCancel={goToOpportunities}
				/>
				<ModalConfirmCreateHierarchy
					isOpen={isModalOpen}
					onConfirm={handleConfirmCreateHierarchy}
					onCancel={handleCloseModal}
				/>
			</>
		);
	}, [
		formInitialState,
		renderRows,
		onSubmit,
		goToOpportunities,
		isLoadingSalesChannelData,
		IsLoadingSalesZone,
		isLoadingCommercialHierarchy,
		isLoadingSalesForce,
		isLoadingMemberData,
		isModalOpen,
		handleCloseModal,
		handleConfirmCreateHierarchy,
	]);

	return renderForm();
};

export default FormCadastrarEditarOportunidade;
